import { useGetApPayeeContactInformTemplate } from '@/context/services/doc_instructions/DocInstructions.service';
import {
  ApPayeeContactInformEmailTemplate,
  InstructionEmailForm,
} from '@/features/ap_payees/components/DocInstructionMenu/types';
import { useTranslation } from '@/i18n';
import { isObjectEmpty } from '@/utils/isObjectEmpty';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

// make hook default template btn

export const useInstructionContentHandler = () => {
  const { t } = useTranslation();
  const methods = useFormContext<InstructionEmailForm>();
  const templateResponse = useGetApPayeeContactInformTemplate();
  const [appliedCustomTemplate, setAppliedCustomTemplate] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const { default_template: defaultTemplate, saved_template: savedTemplate } =
    templateResponse?.data ?? {};
  const isTemplateAvailable = useMemo(
    () => !isObjectEmpty(savedTemplate),
    [savedTemplate]
  );

  const { reset } = methods;
  const setFormValues = useCallback(
    (template: ApPayeeContactInformEmailTemplate) => {
      //using reset to set the form values
      const { body, subject } = template;
      reset({ content: body, subject });
    },
    [reset]
  );

  // Auto-apply default template once on load
  useEffect(() => {
    if (!isObjectEmpty(defaultTemplate))
      reset({ content: defaultTemplate.body, ...defaultTemplate });
  }, [defaultTemplate, reset]);

  const handleApplyTemplate = useCallback(() => {
    if (savedTemplate) {
      setFormValues(savedTemplate);
      setTooltipVisible(true);
      setAppliedCustomTemplate(true);

      // Reset success state after 3 seconds
      setTimeout(() => {
        setAppliedCustomTemplate(false);
        setTooltipVisible(false);
      }, 3000);
    }
  }, [savedTemplate, setFormValues]);

  const templateTooltip = useMemo(() => {
    if (!isTemplateAvailable)
      return t('export_instruction_pdf_apply_template_tooltip');
    if (appliedCustomTemplate)
      return t('export_instruction_pdf_apply_template_success_tooltip');
    return '';
  }, [appliedCustomTemplate, isTemplateAvailable, t]);

  const applyTemplateButtonLabel = useMemo(() => {
    if (appliedCustomTemplate)
      return t('export_instruction_pdf_apply_template_success');
    return t('export_instruction_pdf_apply_template');
  }, [appliedCustomTemplate, t]);

  return {
    handleApplyTemplate,
    isTemplateAvailable,
    templateTooltip,
    applyTemplateButtonLabel,
    appliedCustomTemplate,
    tooltipVisible,
  };
};
